/**
 * Template registry mapping configType enum values to form schemas.
 * Drives the Template Gallery and Guided Form rendering.
 *
 * Requirements: 3.1, 3.2, 3.8
 */

export interface FieldSchema {
  key: string;
  label: string;
  description?: string;
  helpText?: string;
  type: 'text' | 'number' | 'boolean' | 'select' | 'key-value' | 'time' | 'date' | 'textarea' | 'reference';
  required?: boolean;
  defaultValue?: unknown;
  options?: { label: string; value: string }[];
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    patternMessage?: string;
  };
  /** For type: 'reference' — single config type to fetch as dropdown options */
  referencedConfigType?: string;
  /** For type: 'reference' — multiple config types (grouped dropdown) */
  referencedConfigTypes?: string[];
  /** Sibling field key that determines context-aware rendering mode */
  contextField?: string;
  /** Map of context field values to rendering modes */
  contextModes?: Record<string, 'dropdown' | 'e164' | 'text'>;
}

export interface ConfigTemplate {
  configType: string;
  displayName: string;
  description: string;
  payloadSchema: FieldSchema[];
  previewExample?: Record<string, unknown>;
  /** Optional category for grouping related templates in the gallery UI */
  templateCategory?: string;
}

export const templateRegistry: Map<string, ConfigTemplate> = new Map([
  [
    'business-hours',
    {
      configType: 'business-hours',
      displayName: 'Business Hours',
      description: 'Define weekly operating hours for a queue or service',
      payloadSchema: [
        {
          key: 'businessHoursType',
          label: 'Schedule Type',
          type: 'select',
          required: true,
          defaultValue: 'default',
          options: [{ label: 'Default', value: 'default' }],
        },
        { key: 'timeZone', label: 'Timezone', type: 'text', required: true, helpText: 'e.g. Pacific/Auckland' },
        { key: 'monday_enabled', label: 'Monday Enabled', type: 'boolean', defaultValue: true },
        { key: 'monday_startTime', label: 'Monday Start', type: 'time', defaultValue: '08:00' },
        { key: 'monday_endTime', label: 'Monday End', type: 'time', defaultValue: '17:00' },
        { key: 'tuesday_enabled', label: 'Tuesday Enabled', type: 'boolean', defaultValue: true },
        { key: 'tuesday_startTime', label: 'Tuesday Start', type: 'time', defaultValue: '08:00' },
        { key: 'tuesday_endTime', label: 'Tuesday End', type: 'time', defaultValue: '17:00' },
        { key: 'wednesday_enabled', label: 'Wednesday Enabled', type: 'boolean', defaultValue: true },
        { key: 'wednesday_startTime', label: 'Wednesday Start', type: 'time', defaultValue: '08:00' },
        { key: 'wednesday_endTime', label: 'Wednesday End', type: 'time', defaultValue: '17:00' },
        { key: 'thursday_enabled', label: 'Thursday Enabled', type: 'boolean', defaultValue: true },
        { key: 'thursday_startTime', label: 'Thursday Start', type: 'time', defaultValue: '08:00' },
        { key: 'thursday_endTime', label: 'Thursday End', type: 'time', defaultValue: '17:00' },
        { key: 'friday_enabled', label: 'Friday Enabled', type: 'boolean', defaultValue: true },
        { key: 'friday_startTime', label: 'Friday Start', type: 'time', defaultValue: '08:00' },
        { key: 'friday_endTime', label: 'Friday End', type: 'time', defaultValue: '17:00' },
        { key: 'saturday_enabled', label: 'Saturday Enabled', type: 'boolean', defaultValue: false },
        { key: 'saturday_startTime', label: 'Saturday Start', type: 'time' },
        { key: 'saturday_endTime', label: 'Saturday End', type: 'time' },
        { key: 'sunday_enabled', label: 'Sunday Enabled', type: 'boolean', defaultValue: false },
        { key: 'sunday_startTime', label: 'Sunday Start', type: 'time' },
        { key: 'sunday_endTime', label: 'Sunday End', type: 'time' },
      ],
    },
  ],
  [
    'dnis',
    {
      configType: 'dnis',
      displayName: 'DNIS (Inbound Number)',
      description: 'Configure an inbound phone number with routing, recording, and schedule settings',
      payloadSchema: [
        // --- Core ---
        { key: 'areaCode', label: 'Area Code', type: 'select', options: [
          { label: '+64 (NZ)', value: '+64' }, { label: '+61 (AU)', value: '+61' },
        ], helpText: 'Country calling code for this number' },
        { key: 'dnisTag', label: 'DNIS Tag', type: 'text', helpText: 'Flexible attribute to tag this DID' },
        { key: 'agentGreeting', label: 'Agent Greeting', type: 'textarea', helpText: 'Custom greeting played to the agent' },
        { key: 'welcomeMessage', label: 'Welcome Message', type: 'textarea', helpText: 'Message played when call connects' },
        // --- Routing ---
        { key: 'destinationType', label: 'Destination Type', type: 'select', required: true, options: [
          { label: 'Menu', value: 'menu' }, { label: 'Queue', value: 'queue' },
          { label: 'Disconnect', value: 'disconnect' }, { label: 'Deflect', value: 'deflect' },
          { label: 'Transfer', value: 'transfer' }, { label: 'Agent', value: 'agent' },
        ]},
        { key: 'destinationValue', label: 'Destination Value', type: 'reference', referencedConfigTypes: ['menus', 'queues', 'deflections', 'intents'], contextField: 'destinationType', contextModes: { queue: 'dropdown', menu: 'dropdown', transfer: 'e164', agent: 'text', disconnect: 'text', deflect: 'dropdown' }, helpText: 'Select or enter the destination' },
        { key: 'intent', label: 'Default Intent', type: 'reference', referencedConfigType: 'intents', helpText: 'The default intent for this number' },
        // --- Recording & Analytics ---
        { key: 'callRecordingEnabled', label: 'Call Recording', type: 'boolean', defaultValue: true, helpText: 'Turns on call recording for this DNIS' },
        { key: 'contactLensEnabled', label: 'Contact Lens', type: 'boolean', defaultValue: false, helpText: 'Enables Contact Lens transcription' },
        { key: 'contactLensRedaction', label: 'Contact Lens Redaction', type: 'boolean', defaultValue: false, helpText: 'Enables Contact Lens redaction' },
        { key: 'tagAttributesToCTR', label: 'Tag Attributes to CTR', type: 'text', helpText: 'Comma-separated list of contact attributes to tag to the CTR' },
        // --- Voice ---
        { key: 'pollyVoice', label: 'Polly Voice', type: 'select', options: [
          { label: 'Aria', value: 'Aria' }, { label: 'Brian', value: 'Brian' },
          { label: 'Celine', value: 'Celine' }, { label: 'Chantal', value: 'Chantal' },
          { label: 'Emma', value: 'Emma' }, { label: 'Ivy', value: 'Ivy' },
          { label: 'Joanna', value: 'Joanna' }, { label: 'Joey', value: 'Joey' },
          { label: 'Justin', value: 'Justin' }, { label: 'Kendra', value: 'Kendra' },
          { label: 'Kimberly', value: 'Kimberly' }, { label: 'Lea', value: 'Lea' },
          { label: 'Lupe', value: 'Lupe' }, { label: 'Mathieu', value: 'Mathieu' },
          { label: 'Miguel', value: 'Miguel' }, { label: 'Nicole', value: 'Nicole' },
          { label: 'Olivia', value: 'Olivia' }, { label: 'Penelope', value: 'Penelope' },
          { label: 'Ricardo', value: 'Ricardo' }, { label: 'Russell', value: 'Russell' },
          { label: 'Salli', value: 'Salli' }, { label: 'Vitoria', value: 'Vitoria' },
        ], helpText: 'The TTS voice used to speak messaging' },
        // --- Business Hours ---
        { key: 'businessHoursSchedule', label: 'Business Hours Schedule', type: 'reference', referencedConfigType: 'business-hours', helpText: 'Select an existing business-hours configuration' },
        { key: 'afterHoursMessage', label: 'After Hours Message', type: 'textarea', helpText: 'Message played when the queue is closed' },
        { key: 'afterHoursAction', label: 'After Hours Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when call is after hours' },
        // --- Holidays ---
        { key: 'holidaySchedule', label: 'Holiday Schedule', type: 'reference', referencedConfigType: 'holidays', helpText: 'The schedule name passed to holiday check' },
        { key: 'holidayMessage', label: 'Holiday Message', type: 'textarea', helpText: 'Message played when holiday schedule is active' },
        { key: 'holidayAction', label: 'Holiday Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken during a holiday' },
        // --- Emergency ---
        { key: 'emergencyEnabled', label: 'Emergency Mode', type: 'boolean', defaultValue: false, helpText: 'Turns on the emergency feature for this DNIS' },
        { key: 'emergencyMessage', label: 'Emergency Message', type: 'textarea', helpText: 'Message played when emergency is enabled' },
        { key: 'emergencyAction', label: 'Emergency Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
        ], helpText: 'Action taken when emergency condition is enabled' },
        // --- Outage ---
        { key: 'outageEnabled', label: 'Outage Mode', type: 'boolean', defaultValue: false, helpText: 'Turns on the outage feature for this DNIS' },
        { key: 'outageMessage', label: 'Outage Message', type: 'textarea', helpText: 'Message played when outage is enabled' },
        { key: 'chatOutageMessage', label: 'Chat Outage Message', type: 'textarea', helpText: 'Message played when outage is enabled for chat' },
        { key: 'outageAction', label: 'Outage Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
        ], helpText: 'Action taken when outage condition is enabled' },
        // --- Meeting ---
        { key: 'meetingEnabled', label: 'Meeting Mode', type: 'boolean', defaultValue: false, helpText: 'Turns on the meeting condition for this DNIS' },
        { key: 'meetingMessage', label: 'Meeting Message', type: 'textarea', helpText: 'Message played when meeting is enabled' },
        { key: 'meetingAction', label: 'Meeting Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
        ], helpText: 'Action taken when meeting condition is enabled' },
        // --- Blocked Callers ---
        { key: 'blockedCallerEnabled', label: 'Blocked Caller Enabled', type: 'boolean', defaultValue: false, helpText: 'Turns on the blocked caller feature for this DNIS' },
        { key: 'blockedCallerConfig', label: 'Blocked Caller Config', type: 'reference', referencedConfigType: 'blocked', helpText: 'Reference to blocked caller configurations' },
        { key: 'blockedCallerMessage', label: 'Blocked Caller Message', type: 'textarea', helpText: 'Message played to the blocked caller' },
        // --- Transfer ---
        { key: 'externalTransferNumber', label: 'External Transfer Number', type: 'text', helpText: 'Number to transfer to when any emergency, outage, or schedule action is transfer' },
        // --- Repeat Caller ---
        { key: 'repeatCallerEnabled', label: 'Repeat Caller Enabled', type: 'boolean', defaultValue: false, helpText: 'Enable the return caller feature' },
        { key: 'repeatCallerRange', label: 'Repeat Caller Range (days)', type: 'select', options: [
          { label: '1 day', value: '1' }, { label: '3 days', value: '3' },
          { label: '5 days', value: '5' }, { label: '7 days', value: '7' },
          { label: '15 days', value: '15' }, { label: '30 days', value: '30' },
        ], helpText: 'Maximum age in days for previous contacts to be considered recent' },
        // --- Customer Profile ---
        { key: 'customerProfileEnabled', label: 'Customer Profile Enabled', type: 'boolean', defaultValue: false, helpText: 'Retrieve customer information to present to agent prior to connecting' },
        // --- Last Agent ---
        { key: 'lastAgentEnabled', label: 'Last Agent Enabled', type: 'text', helpText: 'Enable the last agent feature (attempts to connect to the last agent)' },
        { key: 'lastAgentRange', label: 'Last Agent Range (days)', type: 'select', options: [
          { label: '1 day', value: '1' }, { label: '3 days', value: '3' },
          { label: '5 days', value: '5' }, { label: '7 days', value: '7' },
          { label: '15 days', value: '15' }, { label: '30 days', value: '30' },
        ], helpText: 'Maximum age in days for previous contacts to be considered for last agent routing' },
        { key: 'lastAgentMessage', label: 'Last Agent Message', type: 'textarea', helpText: 'Prompt asking if the contact wants to be routed to the last agent they spoke to' },
        // --- Callback ---
        { key: 'callbackMaxOffers', label: 'Callback Max Offers', type: 'number', helpText: 'Maximum number of times contacts are offered a callback during the customer queue flow' },
      ],
    },
  ],
  [
    'queues',
    {
      configType: 'queues',
      displayName: 'Queue',
      description: 'Configure a queue with business hours, holidays, emergency, and agent routing settings',
      payloadSchema: [
        // --- Agent Features ---
        { key: 'stepByStepGuideEnabled', label: 'Step-by-Step Guide', type: 'boolean', defaultValue: false, helpText: 'Enable custom agent view' },
        { key: 'bypassMandatoryMessagingForTransfers', label: 'Bypass Mandatory Messaging (Transfers)', type: 'boolean', defaultValue: false, helpText: 'Bypass mandatory messaging for transfers' },
        // --- Voice ---
        { key: 'pollyVoice', label: 'Polly Voice', type: 'select', options: [
          { label: 'Aria', value: 'Aria' }, { label: 'Brian', value: 'Brian' },
          { label: 'Celine', value: 'Celine' }, { label: 'Chantal', value: 'Chantal' },
          { label: 'Emma', value: 'Emma' }, { label: 'Ivy', value: 'Ivy' },
          { label: 'Joanna', value: 'Joanna' }, { label: 'Joey', value: 'Joey' },
          { label: 'Justin', value: 'Justin' }, { label: 'Kendra', value: 'Kendra' },
          { label: 'Kimberly', value: 'Kimberly' }, { label: 'Lea', value: 'Lea' },
          { label: 'Lupe', value: 'Lupe' }, { label: 'Mathieu', value: 'Mathieu' },
          { label: 'Miguel', value: 'Miguel' }, { label: 'Nicole', value: 'Nicole' },
          { label: 'Olivia', value: 'Olivia' }, { label: 'Penelope', value: 'Penelope' },
          { label: 'Ricardo', value: 'Ricardo' }, { label: 'Russell', value: 'Russell' },
          { label: 'Salli', value: 'Salli' }, { label: 'Vitoria', value: 'Vitoria' },
        ], helpText: 'The TTS voice used to speak messaging' },
        // --- Business Hours ---
        { key: 'businessHoursSchedule', label: 'Business Hours Schedule', type: 'reference', referencedConfigType: 'business-hours', required: true, helpText: 'Select an existing business-hours configuration' },
        { key: 'afterHoursMessage', label: 'After Hours Message', type: 'textarea', helpText: 'Message played when the queue is closed' },
        { key: 'chatAfterHoursMessage', label: 'Chat After Hours Message', type: 'textarea', helpText: 'Message played when the queue is closed for chat' },
        { key: 'afterHoursAction', label: 'After Hours Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when call is after hours' },
        // --- Holidays ---
        { key: 'holidaySchedule', label: 'Holiday Schedule', type: 'reference', referencedConfigType: 'holidays', helpText: 'The schedule name passed to holiday check' },
        { key: 'holidayMessage', label: 'Holiday Message', type: 'textarea', helpText: 'Message played when holiday schedule is active' },
        { key: 'chatHolidayMessage', label: 'Chat Holiday Message', type: 'textarea', helpText: 'Message played when holiday schedule is active for chat' },
        { key: 'holidayAction', label: 'Holiday Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken during a holiday' },
        // --- Emergency ---
        { key: 'emergencyEnabled', label: 'Emergency Mode', type: 'boolean', defaultValue: false, helpText: 'Turns on the emergency feature for this queue' },
        { key: 'emergencyMessage', label: 'Emergency Message', type: 'textarea', helpText: 'Message played when emergency is enabled' },
        { key: 'chatEmergencyMessage', label: 'Chat Emergency Message', type: 'textarea', helpText: 'Message played when emergency is enabled for chat' },
        { key: 'emergencyAction', label: 'Emergency Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when emergency condition is enabled' },
        // --- Outage ---
        { key: 'outageEnabled', label: 'Outage Mode', type: 'boolean', defaultValue: false, helpText: 'Turns on the outage feature for this queue' },
        { key: 'outageMessage', label: 'Outage Message', type: 'textarea', helpText: 'Message played when outage is enabled' },
        { key: 'outageAction', label: 'Outage Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when outage condition is enabled' },
        // --- Meeting ---
        { key: 'meetingEnabled', label: 'Meeting Mode', type: 'boolean', defaultValue: false, helpText: 'Turns on the meeting condition for this queue' },
        { key: 'meetingMessage', label: 'Meeting Message', type: 'textarea', helpText: 'Message played when meeting is enabled' },
        { key: 'chatMeetingMessage', label: 'Chat Meeting Message', type: 'textarea', helpText: 'Message played when meeting is enabled for chat' },
        { key: 'meetingAction', label: 'Meeting Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when meeting condition is enabled' },
        // --- Transfer ---
        { key: 'externalTransferNumber', label: 'External Transfer Number', type: 'text', helpText: 'Number to transfer to when any emergency, outage, or schedule action is transfer' },
        // --- Pre-Queue Messaging ---
        { key: 'preQueueMandatoryMessage', label: 'Pre-Queue Mandatory Message', type: 'textarea', helpText: 'Message played prior to queueing (e.g. call recording notice)' },
        { key: 'chatPreQueueMandatoryMessage', label: 'Chat Pre-Queue Message', type: 'textarea', helpText: 'Pre-queue mandatory message for chat' },
        { key: 'emailPreQueueMandatoryMessage', label: 'Email Pre-Queue Message', type: 'textarea', helpText: 'Mandatory message sent to email during pre-queue acknowledgement' },
        // --- No Agents ---
        { key: 'noAgentsStaffedMessage', label: 'No Agents Staffed Message', type: 'textarea', helpText: 'Message played when no agents are staffed' },
        { key: 'chatNoAgentsStaffedMessage', label: 'Chat No Agents Staffed Message', type: 'textarea', helpText: 'Message played when no agents are staffed for chat' },
        { key: 'noAgentsStaffedAction', label: 'No Agents Staffed Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when no agents are staffed' },
        { key: 'noAgentsOnlineMessage', label: 'No Agents Online Message', type: 'textarea', helpText: 'Message played when no agents are online' },
        { key: 'noAgentsOnlineAction', label: 'No Agents Online Action', type: 'select', options: [
          { label: 'Resume', value: 'resume' }, { label: 'Disconnect', value: 'disconnect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when no agents are online' },
        // --- Max Queue Size ---
        { key: 'maxQueueSizeEnabled', label: 'Max Queue Size Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables the max queue size feature' },
        { key: 'maxQueueSize', label: 'Max Queue Size', type: 'number', helpText: 'Maximum calls allowed in queue before overflow' },
        { key: 'maxQueueSizeMessage', label: 'Max Queue Size Message', type: 'textarea', helpText: 'Message played when max queue size threshold is met' },
        { key: 'chatMaxQueueSizeMessage', label: 'Chat Max Queue Size Message', type: 'textarea', helpText: 'Message played when max queue size threshold is met for chat' },
        // --- Whispers & Transfers ---
        { key: 'inboundAgentWhisperMessage', label: 'Agent Whisper Message', type: 'textarea', helpText: 'Whisper played to agent before receiving an inbound call' },
        { key: 'inboundCustomerWhisperMessage', label: 'Customer Whisper Message', type: 'textarea', helpText: 'Whisper played to customer before connecting to an agent' },
        { key: 'transferMessage', label: 'Transfer Message', type: 'textarea', helpText: 'Message played to the person initiating the transfer' },
        { key: 'chatTransferMessage', label: 'Chat Transfer Message', type: 'textarea', helpText: 'Chat transfer message played before connecting to destination' },
        { key: 'agentWhisperPollyVoice', label: 'Agent Whisper Polly Voice', type: 'select', options: [
          { label: 'Aria', value: 'Aria' }, { label: 'Brian', value: 'Brian' },
          { label: 'Celine', value: 'Celine' }, { label: 'Chantal', value: 'Chantal' },
          { label: 'Emma', value: 'Emma' }, { label: 'Ivy', value: 'Ivy' },
          { label: 'Joanna', value: 'Joanna' }, { label: 'Joey', value: 'Joey' },
          { label: 'Justin', value: 'Justin' }, { label: 'Kendra', value: 'Kendra' },
          { label: 'Kimberly', value: 'Kimberly' }, { label: 'Lea', value: 'Lea' },
          { label: 'Lupe', value: 'Lupe' }, { label: 'Mathieu', value: 'Mathieu' },
          { label: 'Miguel', value: 'Miguel' }, { label: 'Nicole', value: 'Nicole' },
          { label: 'Olivia', value: 'Olivia' }, { label: 'Penelope', value: 'Penelope' },
          { label: 'Ricardo', value: 'Ricardo' }, { label: 'Russell', value: 'Russell' },
          { label: 'Salli', value: 'Salli' }, { label: 'Vitoria', value: 'Vitoria' },
        ], helpText: 'TTS voice used for agent whisper if override needed' },
        // --- Agent Hold ---
        { key: 'agentHoldLoopMessage', label: 'Agent Hold Loop Message', type: 'textarea', helpText: 'Message agent hears when customer is on hold' },
        { key: 'agentHoldLoopDuration', label: 'Agent Hold Loop Duration', type: 'select', options: [
          { label: '60 seconds', value: '60' }, { label: '90 seconds', value: '90' },
          { label: '120 seconds', value: '120' }, { label: '180 seconds', value: '180' },
        ], helpText: 'Duration of the hold loop before message repeats' },
        // --- Chat Agent ---
        { key: 'chatAgentNameLogic', label: 'Chat Agent Name Logic', type: 'boolean', defaultValue: false, helpText: 'Enable the use of agent name for whispers' },
        { key: 'chatCustomerWhisperIntroMessage', label: 'Chat Customer Whisper Intro', type: 'textarea', helpText: 'Intro message for chat whisper' },
        { key: 'chatCustomerWhisperOutroMessage', label: 'Chat Customer Whisper Outro', type: 'textarea', helpText: 'Outro message for chat whisper' },
        // --- Announcements ---
        { key: 'announcementsEnabled', label: 'Announcements Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables call queue announcements and chat queue messages' },
        { key: 'announcementsConfig', label: 'Announcements Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for queue announcement messages' },
        // --- Callback ---
        { key: 'callbackEnabled', label: 'Callback Enabled', type: 'boolean', defaultValue: false, helpText: 'Enable callback functionality for this queue' },
        { key: 'callbackConfig', label: 'Callback Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for the callback experience' },
        // --- Queue Status ---
        { key: 'queueStatusEnabled', label: 'Queue Status Enabled', type: 'boolean', defaultValue: false, helpText: 'Inform customer of estimated wait time and position in queue' },
        { key: 'queueStatusConfig', label: 'Queue Status Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for queue status information' },
        // --- Post Call Survey ---
        { key: 'postCallSurveyEnabled', label: 'Post Call Survey Enabled', type: 'boolean', defaultValue: false, helpText: 'Turns on post call survey for this queue' },
        { key: 'postCallSurveyConfig', label: 'Post Call Survey Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for the post call survey' },
        // --- Agent Queue Escalation ---
        { key: 'agentQueueEscalationEnabled', label: 'Agent Queue Escalation', type: 'boolean', defaultValue: false, helpText: 'Offer contacts in agent queue to escalate to wider queue' },
        { key: 'agentQueueEscalationConfig', label: 'Agent Queue Escalation Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for agent queue escalation' },
        // --- Recording & Analytics ---
        { key: 'dynamicCallerID', label: 'Dynamic Caller ID', type: 'boolean', defaultValue: false, helpText: 'Turns on dynamic caller ID for this queue' },
        { key: 'callRecordingEnabled', label: 'Call Recording', type: 'boolean', defaultValue: true, helpText: 'Turns on call recording for this queue' },
        { key: 'contactLensRedaction', label: 'Contact Lens Redaction', type: 'boolean', defaultValue: false, helpText: 'Enables Contact Lens redaction' },
        { key: 'contactLensEnabled', label: 'Contact Lens', type: 'boolean', defaultValue: false, helpText: 'Enables Contact Lens transcription' },
        { key: 'outboundCallerId', label: 'Outbound Caller ID', type: 'text', helpText: 'Number displayed as caller ID when masking is enabled' },
        { key: 'chatCustomerQueueLoopDurationInSeconds', label: 'Chat Queue Loop Duration (seconds)', type: 'number', helpText: 'Loop wait time in seconds for customer queue waiting loop' },
        { key: 'tagAttributesToCTR', label: 'Tag Attributes to CTR', type: 'text', helpText: 'Comma-separated list of contact attributes to tag to the CTR' },
      ],
    },
  ],
  [
    'menus',
    {
      configType: 'menus',
      displayName: 'IVR Menu',
      description: 'Define an IVR menu with prompt and option-to-intent mappings (up to 8 options)',
      payloadSchema: [
        { key: 'menuPrompt', label: 'Menu Prompt', type: 'textarea', required: true, helpText: 'The message played to the caller' },
        { key: 'option1Intent', label: 'Option 1 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option2Intent', label: 'Option 2 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option3Intent', label: 'Option 3 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option4Intent', label: 'Option 4 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option5Intent', label: 'Option 5 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option6Intent', label: 'Option 6 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option7Intent', label: 'Option 7 Intent', type: 'reference', referencedConfigType: 'intents' },
        { key: 'option8Intent', label: 'Option 8 Intent', type: 'reference', referencedConfigType: 'intents' },
      ],
    },
  ],
  [
    'intents',
    {
      configType: 'intents',
      displayName: 'Intent',
      description: 'Define a routing intent with destination, conditional logic, and loop escape settings',
      payloadSchema: [
        // --- Primary Destination ---
        { key: 'destinationType', label: 'Destination Type', type: 'select', required: true, options: [
          { label: 'Menu', value: 'menu' }, { label: 'Queue', value: 'queue' },
          { label: 'Disconnect', value: 'disconnect' }, { label: 'Deflect', value: 'deflect' },
          { label: 'Transfer', value: 'transfer' }, { label: 'Agent', value: 'agent' },
        ]},
        { key: 'destinationValue', label: 'Destination Value', type: 'text', required: true, helpText: 'Destination unique identifier' },
        { key: 'conditionalExpression', label: 'Conditional Expression', type: 'text', helpText: 'If empty, resolves to true and routes to destination' },
        // --- Intent Loop Escape ---
        { key: 'intentLoopDetectionThreshold', label: 'Intent Loop Threshold', type: 'number', defaultValue: 3, helpText: 'Times the same intent is validated before considered a loop' },
        { key: 'intentLoopEscapeDestinationType', label: 'Intent Loop Escape Type', type: 'select', options: [
          { label: 'Menu', value: 'menu' }, { label: 'Queue', value: 'queue' },
          { label: 'Disconnect', value: 'disconnect' }, { label: 'Deflect', value: 'deflect' },
          { label: 'Transfer', value: 'transfer' }, { label: 'Agent', value: 'agent' },
        ], helpText: 'Escape destination when intent loop is detected' },
        { key: 'intentLoopEscapeDestinationValue', label: 'Intent Loop Escape Value', type: 'text', helpText: 'Escape destination identifier for intent loop' },
        // --- Rule Loop Escape ---
        { key: 'ruleLoopDetectionThreshold', label: 'Rule Loop Threshold', type: 'number', defaultValue: 3, helpText: 'Times the same rule is validated before considered a loop' },
        { key: 'ruleLoopEscapeDestinationType', label: 'Rule Loop Escape Type', type: 'select', options: [
          { label: 'Menu', value: 'menu' }, { label: 'Queue', value: 'queue' },
          { label: 'Disconnect', value: 'disconnect' }, { label: 'Deflect', value: 'deflect' },
          { label: 'Transfer', value: 'transfer' }, { label: 'Agent', value: 'agent' },
        ], helpText: 'Escape destination when rule loop is detected' },
        { key: 'ruleLoopEscapeDestinationValue', label: 'Rule Loop Escape Value', type: 'text', helpText: 'Escape destination identifier for rule loop' },
      ],
    },
  ],
  [
    'holidays',
    {
      configType: 'holidays',
      displayName: 'Holiday / Closure',
      description: 'Define holiday dates and closure periods for a service or queue',
      payloadSchema: [
        { key: 'timeZone', label: 'Timezone', type: 'text', helpText: 'e.g. Pacific/Auckland' },
      ],
    },
  ],
  [
    'deflections',
    {
      configType: 'deflections',
      displayName: 'Deflection',
      description: 'Configure call/chat deflection messaging and fallback actions',
      payloadSchema: [
        // --- Voice Deflection ---
        { key: 'deflectionMessage', label: 'Deflection Message', type: 'textarea', required: true, helpText: 'Deflection message for voice channel' },
        { key: 'bypassDeflectionMessage', label: 'Bypass Deflection', type: 'boolean', defaultValue: false, helpText: 'Bypass deflection message and go straight to agent transfer' },
        { key: 'failedDeflectionAction', label: 'Failed Deflection Action', type: 'select', options: [
          { label: 'Menu', value: 'menu' }, { label: 'Queue', value: 'queue' },
          { label: 'Disconnect', value: 'disconnect' }, { label: 'Deflect', value: 'deflect' },
          { label: 'Transfer', value: 'transfer' },
        ], helpText: 'Action taken when deflection is unsuccessful' },
        { key: 'failedDeflectionValue', label: 'Failed Deflection Value', type: 'reference', referencedConfigType: 'queues', helpText: 'Destination if deflection is unsuccessful' },
        // --- Chat Deflection ---
        { key: 'chatDeflectionMessage', label: 'Chat Deflection Message', type: 'textarea', helpText: 'Deflection message for chat channel' },
        { key: 'chatFailedDeflectionAction', label: 'Chat Failed Deflection Action', type: 'select', options: [
          { label: 'Menu', value: 'menu' }, { label: 'Queue', value: 'queue' },
          { label: 'Disconnect', value: 'disconnect' }, { label: 'Deflect', value: 'deflect' },
        ], helpText: 'Action taken when chat deflection is unsuccessful' },
        { key: 'chatFailedDeflectionValue', label: 'Chat Failed Deflection Value', type: 'reference', referencedConfigType: 'queues', helpText: 'Destination if chat deflection is unsuccessful' },
      ],
    },
  ],
  [
    'chats',
    {
      configType: 'chats',
      displayName: 'Chat',
      description: 'Configure a chat channel with welcome message and queue routing',
      payloadSchema: [
        { key: 'welcomeMessage', label: 'Welcome Message', type: 'textarea', required: true },
        { key: 'mainMenuPrompt', label: 'Main Menu Prompt', type: 'textarea' },
        { key: 'queueName', label: 'Queue Name', type: 'reference', referencedConfigType: 'queues', required: true },
      ],
    },
  ],
  [
    'emails',
    {
      configType: 'emails',
      displayName: 'Email',
      description: 'Configure an email channel with templates, routing, and customer profile settings',
      payloadSchema: [
        { key: 'welcomeMessage', label: 'Acknowledgement Message', type: 'textarea', helpText: 'Early acknowledgement message for the email destination' },
        { key: 'emailTemplate', label: 'Email Template', type: 'select', options: [
          { label: 'Tower', value: 'Tower' }, { label: 'TradeMe', value: 'TradeMe' },
        ], helpText: 'Email template used for acknowledgement message' },
        { key: 'emailPreQueueTemplate', label: 'Pre-Queue Email Template', type: 'select', options: [
          { label: 'Tower', value: 'Tower' }, { label: 'TradeMe', value: 'TradeMe' },
        ], helpText: 'Email template used for acknowledgement at the pre-queue level' },
        { key: 'emailTag', label: 'Email Tag', type: 'text', helpText: 'Flexible attribute to tag an email destination' },
        { key: 'intent', label: 'Intent', type: 'reference', referencedConfigType: 'intents', helpText: 'The intent configuration for routing this email' },
        { key: 'destinationType', label: 'Destination Type', type: 'select', required: true, options: [
          { label: 'Queue', value: 'queue' }, { label: 'Agent', value: 'agent' },
        ], helpText: 'Action taken when an email comes in' },
        { key: 'destinationValue', label: 'Destination Value', type: 'text', required: true, helpText: 'Destination unique identifier' },
        // --- Blocked Callers ---
        { key: 'blockedCallerEnabled', label: 'Blocked Caller Enabled', type: 'boolean', defaultValue: false, helpText: 'Turns on the blocked caller feature for this email destination' },
        { key: 'blockedCallerConfig', label: 'Blocked Caller Config', type: 'reference', referencedConfigType: 'blocked', helpText: 'Reference to blocked caller address list' },
        { key: 'blockedKeywordConfig', label: 'Blocked Keyword Config', type: 'reference', referencedConfigType: 'blocked', helpText: 'Reference to blocked keyword list' },
        { key: 'blockedCallerMessage', label: 'Blocked Caller Message', type: 'textarea', helpText: 'Message sent when a blocked caller emails' },
        // --- Customer Profile ---
        { key: 'customerProfileEnabled', label: 'Customer Profile Enabled', type: 'boolean', defaultValue: false, helpText: 'Turns on customer profile identification process' },
      ],
    },
  ],
  [
    'blocked',
    {
      configType: 'blocked',
      displayName: 'Blocked Callers/Keywords',
      description: 'Define blocked caller numbers or keywords',
      payloadSchema: [
        { key: 'blockedEntries', label: 'Blocked Entries', type: 'key-value', required: true, helpText: 'Key: identifier, Value: phone number or keyword' },
        { key: 'blockedCallerMessage', label: 'Blocked Caller Message', type: 'textarea' },
      ],
    },
  ],
  [
    'dnisMapping',
    {
      configType: 'dnisMapping',
      displayName: 'Dialled Number Mapping',
      description: 'Map a dialled number (DID) to a target DNIS configuration',
      payloadSchema: [
        { key: 'targetDnisConfig', label: 'Target DNIS Config', type: 'reference', referencedConfigType: 'dnis', required: true, helpText: 'The DNIS configuration this dialled number maps to' },
      ],
    },
  ],
  // ─── Queue Features: Generic fallback (for viewing/editing existing configs with mixed fields) ───
  [
    'queue-features',
    {
      configType: 'queue-features',
      displayName: 'Queue Features (All)',
      description: 'Generic queue features template with all fields — used as fallback for viewing/editing existing configs',
      templateCategory: 'Queue Features',
      payloadSchema: [
        // --- Announcements ---
        { key: 'callAnnouncementMessage1', label: 'Call Announcement 1', type: 'textarea', helpText: 'First announcement message played to customer' },
        { key: 'callAnnouncementMessage2', label: 'Call Announcement 2', type: 'textarea', helpText: 'Second announcement message' },
        { key: 'callAnnouncementMessage3', label: 'Call Announcement 3', type: 'textarea', helpText: 'Third announcement message' },
        { key: 'callAnnouncementMessage4', label: 'Call Announcement 4', type: 'textarea', helpText: 'Fourth announcement message' },
        { key: 'callAnnouncementMessage5', label: 'Call Announcement 5', type: 'textarea', helpText: 'Fifth announcement message' },
        { key: 'callAnnouncementMessage6', label: 'Call Announcement 6', type: 'textarea', helpText: 'Sixth announcement message' },
        { key: 'callAnnouncementMessage7', label: 'Call Announcement 7', type: 'textarea', helpText: 'Seventh announcement message' },
        { key: 'callAnnouncementMessage8', label: 'Call Announcement 8', type: 'textarea', helpText: 'Eighth announcement message' },
        { key: 'chatAnnouncementMessage1', label: 'Chat Announcement 1', type: 'textarea', helpText: 'First chat queue message' },
        { key: 'chatAnnouncementMessage2', label: 'Chat Announcement 2', type: 'textarea', helpText: 'Second chat queue message' },
        { key: 'chatAnnouncementMessage3', label: 'Chat Announcement 3', type: 'textarea', helpText: 'Third chat queue message' },
        { key: 'chatAnnouncementMessage4', label: 'Chat Announcement 4', type: 'textarea', helpText: 'Fourth chat queue message' },
        // --- Callbacks ---
        { key: 'callbackQueueName', label: 'Callback Queue', type: 'reference', referencedConfigType: 'queues', helpText: 'Queue that callbacks will be routed to' },
        { key: 'callbackMinimumWaitTimePrequeueInSeconds', label: 'Callback Min Wait (Pre-Queue)', type: 'number', helpText: 'Minimum seconds oldest contact must be in queue to trigger callback offer (pre-queue)' },
        { key: 'callbackMinimumWaitTimeCustomerQueueInSeconds', label: 'Callback Min Wait (Customer Queue)', type: 'number', helpText: 'Minimum seconds current contact must be in queue before callback offer' },
        { key: 'callbackHoursSchedule', label: 'Callback Hours Schedule', type: 'reference', referencedConfigType: 'business-hours', helpText: 'Business hours schedule for callbacks' },
        { key: 'maxContactsForCallback', label: 'Max Contacts for Callback', type: 'number', helpText: 'Maximum contacts offered for callback' },
        { key: 'callbackMenu', label: 'Callback Menu Prompt', type: 'textarea', helpText: 'Menu played to customer when offering a callback' },
        { key: 'callerIDValidationFail', label: 'Caller ID Validation Fail', type: 'textarea', helpText: 'Message played when provided callback number is invalid' },
        { key: 'callbackSuccessMessage', label: 'Callback Success Message', type: 'textarea', helpText: 'Message played after callback is successfully submitted' },
        { key: 'callbackFailureMessage', label: 'Callback Failure Message', type: 'textarea', helpText: 'Message played when callback cannot be created due to error' },
        { key: 'callbackPhoneLocalInputPrompt', label: 'Callback Phone (Local) Prompt', type: 'textarea', helpText: 'Prompt for local phone number input' },
        { key: 'callbackPhoneIntInputPrompt', label: 'Callback Phone (International) Prompt', type: 'textarea', helpText: 'Prompt for international phone number input' },
        { key: 'callbackMaxAttempts', label: 'Callback Max Attempts', type: 'number', helpText: 'Max retry for reinserting a callback using answering machine flow' },
        // --- Queue Status ---
        { key: 'expectedWaitTimeEnabled', label: 'Expected Wait Time Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables wait time features' },
        { key: 'positionInQueueEnabled', label: 'Position in Queue Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables position in queue feature' },
        { key: 'piqEmptyQueueMessage', label: 'PIQ Empty Queue Message', type: 'textarea', helpText: 'Message played when queue is empty' },
        { key: 'piqPrefixMessage', label: 'PIQ Prefix Message', type: 'textarea', helpText: 'Message before position number' },
        { key: 'piqSuffixMessage', label: 'PIQ Suffix Message', type: 'textarea', helpText: 'Message after position number' },
        { key: 'chatPIQEmptyQueueMessage', label: 'Chat PIQ Empty Queue', type: 'textarea', helpText: 'Chat message when queue is empty' },
        { key: 'chatPIQPrefixMessage', label: 'Chat PIQ Prefix', type: 'textarea', helpText: 'Chat message before position number' },
        { key: 'chatPIQSuffixMessage', label: 'Chat PIQ Suffix', type: 'textarea', helpText: 'Chat message after position number' },
        { key: 'ewt0to1minutes', label: 'EWT 0–1 min', type: 'textarea', helpText: 'Wait time message for 0–1 minutes' },
        { key: 'ewt1to2minutes', label: 'EWT 1–2 min', type: 'textarea', helpText: 'Wait time message for 1–2 minutes' },
        { key: 'ewt2to3minutes', label: 'EWT 2–3 min', type: 'textarea', helpText: 'Wait time message for 2–3 minutes' },
        { key: 'ewt3to4minutes', label: 'EWT 3–4 min', type: 'textarea', helpText: 'Wait time message for 3–4 minutes' },
        { key: 'ewt4to5minutes', label: 'EWT 4–5 min', type: 'textarea', helpText: 'Wait time message for 4–5 minutes' },
        { key: 'ewt5to10minutes', label: 'EWT 5–10 min', type: 'textarea', helpText: 'Wait time message for 5–10 minutes' },
        { key: 'ewt10to15minutes', label: 'EWT 10–15 min', type: 'textarea', helpText: 'Wait time message for 10–15 minutes' },
        { key: 'ewt15to20minutes', label: 'EWT 15–20 min', type: 'textarea', helpText: 'Wait time message for 15–20 minutes' },
        { key: 'ewt20to25minutes', label: 'EWT 20–25 min', type: 'textarea', helpText: 'Wait time message for 20–25 minutes' },
        { key: 'ewt25to30minutes', label: 'EWT 25–30 min', type: 'textarea', helpText: 'Wait time message for 25–30 minutes' },
        { key: 'ewt30to35minutes', label: 'EWT 30–35 min', type: 'textarea', helpText: 'Wait time message for 30–35 minutes' },
        { key: 'ewt35to40minutes', label: 'EWT 35–40 min', type: 'textarea', helpText: 'Wait time message for 35–40 minutes' },
        { key: 'ewt40to45minutes', label: 'EWT 40–45 min', type: 'textarea', helpText: 'Wait time message for 40–45 minutes' },
        { key: 'ewt45to50minutes', label: 'EWT 45–50 min', type: 'textarea', helpText: 'Wait time message for 45–50 minutes' },
        { key: 'ewt50to60minutes', label: 'EWT 50–60 min', type: 'textarea', helpText: 'Wait time message for 50–60 minutes' },
        { key: 'ewt60to90minutes', label: 'EWT 60–90 min', type: 'textarea', helpText: 'Wait time message for 60–90 minutes' },
        // --- Post Call Survey ---
        { key: 'postCallSurveyPreQueueMessage', label: 'Survey Pre-Queue Message', type: 'textarea', helpText: 'Message played pre-queue when survey is enabled' },
        { key: 'chatPostCallSurveyPreQueueMessage', label: 'Chat Survey Pre-Queue Message', type: 'textarea', helpText: 'Chat message pre-queue when survey is enabled' },
        { key: 'postCallSurveyWelcome', label: 'Survey Welcome', type: 'textarea', helpText: 'Welcome message at start of survey' },
        { key: 'postCallSurveyGoodbye', label: 'Survey Goodbye', type: 'textarea', helpText: 'Message after all survey questions' },
        { key: 'postCallSurveyQuestion1', label: 'Survey Question 1', type: 'textarea' },
        { key: 'postCallSurveyQuestion2', label: 'Survey Question 2', type: 'textarea' },
        { key: 'postCallSurveyQuestion3', label: 'Survey Question 3', type: 'textarea' },
        { key: 'postCallSurveyQuestion4', label: 'Survey Question 4', type: 'textarea' },
        { key: 'postCallSurveyQuestion5', label: 'Survey Question 5', type: 'textarea' },
        { key: 'postCallSurveyNumberOfQuestions', label: 'Number of Questions', type: 'select', options: [
          { label: '1', value: '1' }, { label: '2', value: '2' }, { label: '3', value: '3' },
          { label: '4', value: '4' }, { label: '5', value: '5' },
        ], helpText: 'Number of questions for the post call survey (1–5)' },
        // --- Agent Queue Escalation ---
        { key: 'agentQueueEscalationMinimumWaitTimeCustomerQueueInSeconds', label: 'Escalation Min Wait (Customer Queue)', type: 'number', helpText: 'Minimum seconds before escalation offer in customer queue' },
        { key: 'agentQueueEscalationMinimumWaitTimePrequeueInSeconds', label: 'Escalation Min Wait (Pre-Queue)', type: 'number', helpText: 'Minimum seconds oldest contact in agent queue to trigger escalation offer' },
        { key: 'agentQueueEscalationMaxOffers', label: 'Escalation Max Offers', type: 'number', helpText: 'Maximum times escalation is offered during customer queue flow' },
        { key: 'agentQueueEscalationNoAgentsStaffedMessage', label: 'Escalation No Agents Message', type: 'textarea', helpText: 'Message when escalation is forced due to no agents staffing agent queue' },
        { key: 'agentQueueEscalationMessage', label: 'Escalation Offer Message', type: 'textarea', helpText: 'Prompt asking if contact wants to escalate to wider queue' },
        { key: 'agentQueueEscalationSuccess', label: 'Escalation Success Message', type: 'textarea', helpText: 'Message when escalation succeeds' },
        { key: 'agentQueueEscalationFailure', label: 'Escalation Failure Message', type: 'textarea', helpText: 'Message when escalation fails' },
      ],
    },
  ],
  // ─── Queue Features: Announcements sub-type ───
  [
    'queue-features-announcements',
    {
      configType: 'queue-features',
      displayName: 'Queue Features — Announcements',
      description: 'Configure call and chat announcement messages played to customers while waiting in queue',
      templateCategory: 'Queue Features',
      payloadSchema: [
        { key: 'callAnnouncementMessage1', label: 'Call Announcement 1', type: 'textarea', helpText: 'First announcement message played to customer' },
        { key: 'callAnnouncementMessage2', label: 'Call Announcement 2', type: 'textarea', helpText: 'Second announcement message' },
        { key: 'callAnnouncementMessage3', label: 'Call Announcement 3', type: 'textarea', helpText: 'Third announcement message' },
        { key: 'chatAnnouncementMessage1', label: 'Chat Announcement 1', type: 'textarea', helpText: 'First chat queue message' },
        { key: 'chatAnnouncementMessage2', label: 'Chat Announcement 2', type: 'textarea', helpText: 'Second chat queue message' },
        { key: 'chatAnnouncementMessage3', label: 'Chat Announcement 3', type: 'textarea', helpText: 'Third chat queue message' },
        { key: 'chatAnnouncementMessage4', label: 'Chat Announcement 4', type: 'textarea', helpText: 'Fourth chat queue message' },
      ],
    },
  ],
  // ─── Queue Features: Callbacks sub-type ───
  [
    'queue-features-callbacks',
    {
      configType: 'queue-features',
      displayName: 'Queue Features — Callbacks',
      description: 'Configure callback offers including wait thresholds, schedules, and messaging',
      templateCategory: 'Queue Features',
      payloadSchema: [
        { key: 'callbackQueueName', label: 'Callback Queue', type: 'reference', referencedConfigType: 'queues', helpText: 'Queue that callbacks will be routed to' },
        { key: 'callbackExpectedWaitTimeThresholdInSeconds', label: 'Callback EWT Threshold (seconds)', type: 'number', helpText: 'Expected wait time threshold in seconds before offering callback' },
        { key: 'callbackHoursSchedule', label: 'Callback Hours Schedule', type: 'reference', referencedConfigType: 'business-hours', helpText: 'Business hours schedule for callbacks' },
        { key: 'callbackMaxOffers', label: 'Callback Max Offers', type: 'number', helpText: 'Maximum number of times contacts are offered a callback during the customer queue flow' },
        { key: 'callbackMenu', label: 'Callback Menu Prompt', type: 'textarea', helpText: 'Menu played to customer when offering a callback' },
        { key: 'callbackMinimumWaitTimeCustomerQueueInSeconds', label: 'Callback Min Wait (Customer Queue)', type: 'number', helpText: 'Minimum seconds current contact must be in queue before callback offer' },
        { key: 'callbackMinimumWaitTimePrequeueInSeconds', label: 'Callback Min Wait (Pre-Queue)', type: 'number', helpText: 'Minimum seconds oldest contact must be in queue to trigger callback offer (pre-queue)' },
        { key: 'callbackSuccessMessage', label: 'Callback Success Message', type: 'textarea', helpText: 'Message played after callback is successfully submitted' },
        { key: 'maxContactsForCallback', label: 'Max Contacts for Callback', type: 'number', helpText: 'Maximum contacts offered for callback' },
      ],
    },
  ],
  // ─── Queue Features: Queue Status sub-type ───
  [
    'queue-features-queue-status',
    {
      configType: 'queue-features',
      displayName: 'Queue Features — Queue Status',
      description: 'Configure expected wait time and position-in-queue messaging for customers',
      templateCategory: 'Queue Features',
      payloadSchema: [
        { key: 'expectedWaitTimeEnabled', label: 'Expected Wait Time Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables wait time messaging features' },
        { key: 'piqPrefixMessage', label: 'PIQ Prefix Message', type: 'textarea', helpText: 'Message before position number (e.g. "You are number")' },
        { key: 'piqSuffixMessage', label: 'PIQ Suffix Message', type: 'textarea', helpText: 'Message after position number (e.g. "in the queue")' },
      ],
    },
  ],
  // ─── Queue Features: Agent Queue Escalation sub-type ───
  [
    'queue-features-agent-queue-escalation',
    {
      configType: 'queue-features',
      displayName: 'Queue Features — Agent Queue Escalation',
      description: 'Configure escalation from agent-specific queues to wider queues when wait times exceed thresholds',
      templateCategory: 'Queue Features',
      payloadSchema: [
        { key: 'agentQueueEscalationMaxOffers', label: 'Escalation Max Offers', type: 'number', helpText: 'Maximum times escalation is offered during customer queue flow' },
        { key: 'agentQueueEscalationMessage', label: 'Escalation Offer Message', type: 'textarea', helpText: 'Prompt asking if contact wants to escalate to wider queue' },
        { key: 'agentQueueEscalationMinimumWaitTimeCustomerQueueInSeconds', label: 'Escalation Min Wait (Customer Queue)', type: 'number', helpText: 'Minimum seconds before escalation offer in customer queue' },
        { key: 'agentQueueEscalationMinimumWaitTimePrequeueInSeconds', label: 'Escalation Min Wait (Pre-Queue)', type: 'number', helpText: 'Minimum seconds oldest contact in agent queue to trigger escalation offer' },
      ],
    },
  ],
  // ─── Queue Features: Surveys sub-type ───
  [
    'queue-features-surveys',
    {
      configType: 'queue-features',
      displayName: 'Queue Features — Surveys',
      description: 'Configure post-call survey questions and messaging (placeholder — no UAT data currently)',
      templateCategory: 'Queue Features',
      payloadSchema: [
        { key: 'postCallSurveyPreQueueMessage', label: 'Survey Pre-Queue Message', type: 'textarea', helpText: 'Message played pre-queue when survey is enabled' },
        { key: 'chatPostCallSurveyPreQueueMessage', label: 'Chat Survey Pre-Queue Message', type: 'textarea', helpText: 'Chat message pre-queue when survey is enabled' },
        { key: 'postCallSurveyWelcome', label: 'Survey Welcome', type: 'textarea', helpText: 'Welcome message at start of survey' },
        { key: 'postCallSurveyGoodbye', label: 'Survey Goodbye', type: 'textarea', helpText: 'Message after all survey questions' },
        { key: 'postCallSurveyQuestion1', label: 'Survey Question 1', type: 'textarea' },
        { key: 'postCallSurveyQuestion2', label: 'Survey Question 2', type: 'textarea' },
        { key: 'postCallSurveyQuestion3', label: 'Survey Question 3', type: 'textarea' },
        { key: 'postCallSurveyQuestion4', label: 'Survey Question 4', type: 'textarea' },
        { key: 'postCallSurveyQuestion5', label: 'Survey Question 5', type: 'textarea' },
        { key: 'postCallSurveyNumberOfQuestions', label: 'Number of Questions', type: 'select', options: [
          { label: '1', value: '1' }, { label: '2', value: '2' }, { label: '3', value: '3' },
          { label: '4', value: '4' }, { label: '5', value: '5' },
        ], helpText: 'Number of questions for the post call survey (1–5)' },
      ],
    },
  ],
  [
    'agentQueues',
    {
      configType: 'agentQueues',
      displayName: 'Queues - Agents',
      description: 'Configure an agent-specific queue with whispers, announcements, and callback settings',
      payloadSchema: [
        // --- Agent Features ---
        { key: 'stepByStepGuideEnabled', label: 'Step-by-Step Guide', type: 'boolean', defaultValue: false, helpText: 'Enable custom agent view' },
        // --- Voice ---
        { key: 'pollyVoice', label: 'Polly Voice', type: 'select', options: [
          { label: 'Aria', value: 'Aria' }, { label: 'Brian', value: 'Brian' },
          { label: 'Celine', value: 'Celine' }, { label: 'Chantal', value: 'Chantal' },
          { label: 'Emma', value: 'Emma' }, { label: 'Ivy', value: 'Ivy' },
          { label: 'Joanna', value: 'Joanna' }, { label: 'Joey', value: 'Joey' },
          { label: 'Justin', value: 'Justin' }, { label: 'Kendra', value: 'Kendra' },
          { label: 'Kimberly', value: 'Kimberly' }, { label: 'Lea', value: 'Lea' },
          { label: 'Lupe', value: 'Lupe' }, { label: 'Mathieu', value: 'Mathieu' },
          { label: 'Miguel', value: 'Miguel' }, { label: 'Nicole', value: 'Nicole' },
          { label: 'Olivia', value: 'Olivia' }, { label: 'Penelope', value: 'Penelope' },
          { label: 'Ricardo', value: 'Ricardo' }, { label: 'Russell', value: 'Russell' },
          { label: 'Salli', value: 'Salli' }, { label: 'Vitoria', value: 'Vitoria' },
        ], helpText: 'The TTS voice used to speak messaging' },
        // --- Transfer ---
        { key: 'externalTransferNumber', label: 'External Transfer Number', type: 'text', helpText: 'Number to transfer to when any emergency, outage, or schedule action is transfer' },
        // --- Pre-Queue Messaging ---
        { key: 'preQueueMandatoryMessage', label: 'Pre-Queue Mandatory Message', type: 'textarea', helpText: 'Message played prior to queueing (e.g. call recording notice)' },
        { key: 'chatPreQueueMandatoryMessage', label: 'Chat Pre-Queue Message', type: 'textarea', helpText: 'Pre-queue mandatory message for chat' },
        // --- Max Queue Size ---
        { key: 'maxQueueSizeEnabled', label: 'Max Queue Size Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables the max queue size feature' },
        { key: 'maxQueueSize', label: 'Max Queue Size', type: 'number', helpText: 'Maximum calls allowed in queue before overflow' },
        { key: 'maxQueueSizeMessage', label: 'Max Queue Size Message', type: 'textarea', helpText: 'Message played when max queue size threshold is met' },
        { key: 'chatMaxQueueSizeMessage', label: 'Chat Max Queue Size Message', type: 'textarea', helpText: 'Message for chat when max queue size is met' },
        // --- Whispers & Transfers ---
        { key: 'inboundAgentWhisperMessage', label: 'Agent Whisper Message', type: 'textarea', helpText: 'Whisper played to agent before receiving an inbound call' },
        { key: 'inboundCustomerWhisperMessage', label: 'Customer Whisper Message', type: 'textarea', helpText: 'Whisper played to customer before connecting to an agent' },
        { key: 'transferMessage', label: 'Transfer Message', type: 'textarea', helpText: 'Message played to the person initiating the transfer' },
        { key: 'chatTransferMessage', label: 'Chat Transfer Message', type: 'textarea', helpText: 'Chat transfer message before connecting to destination' },
        { key: 'agentWhisperPollyVoice', label: 'Agent Whisper Polly Voice', type: 'select', options: [
          { label: 'Aria', value: 'Aria' }, { label: 'Brian', value: 'Brian' },
          { label: 'Celine', value: 'Celine' }, { label: 'Chantal', value: 'Chantal' },
          { label: 'Emma', value: 'Emma' }, { label: 'Ivy', value: 'Ivy' },
          { label: 'Joanna', value: 'Joanna' }, { label: 'Joey', value: 'Joey' },
          { label: 'Justin', value: 'Justin' }, { label: 'Kendra', value: 'Kendra' },
          { label: 'Kimberly', value: 'Kimberly' }, { label: 'Lea', value: 'Lea' },
          { label: 'Lupe', value: 'Lupe' }, { label: 'Mathieu', value: 'Mathieu' },
          { label: 'Miguel', value: 'Miguel' }, { label: 'Nicole', value: 'Nicole' },
          { label: 'Olivia', value: 'Olivia' }, { label: 'Penelope', value: 'Penelope' },
          { label: 'Ricardo', value: 'Ricardo' }, { label: 'Russell', value: 'Russell' },
          { label: 'Salli', value: 'Salli' }, { label: 'Vitoria', value: 'Vitoria' },
        ], helpText: 'TTS voice used for agent whisper if override needed' },
        // --- Agent Hold ---
        { key: 'agentHoldLoopMessage', label: 'Agent Hold Loop Message', type: 'textarea', helpText: 'Message agent hears when customer is on hold' },
        { key: 'agentHoldLoopDuration', label: 'Agent Hold Loop Duration', type: 'select', options: [
          { label: '60 seconds', value: '60' }, { label: '90 seconds', value: '90' },
          { label: '120 seconds', value: '120' }, { label: '180 seconds', value: '180' },
        ], helpText: 'Duration of the hold loop before message repeats' },
        // --- Chat Agent ---
        { key: 'chatAgentNameLogic', label: 'Chat Agent Name Logic', type: 'boolean', defaultValue: false, helpText: 'Enable the use of agent name for whispers' },
        { key: 'chatCustomerWhisperIntroMessage', label: 'Chat Customer Whisper Intro', type: 'textarea', helpText: 'Intro message for chat whisper' },
        { key: 'chatCustomerWhisperOutroMessage', label: 'Chat Customer Whisper Outro', type: 'textarea', helpText: 'Outro message for chat whisper' },
        // --- Announcements ---
        { key: 'announcementsEnabled', label: 'Announcements Enabled', type: 'boolean', defaultValue: false, helpText: 'Enables call queue announcements and chat queue messages' },
        { key: 'announcementsConfig', label: 'Announcements Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for queue announcement messages' },
        // --- Callback ---
        { key: 'callbackEnabled', label: 'Callback Enabled', type: 'boolean', defaultValue: false, helpText: 'Enable callback functionality for this queue' },
        { key: 'callbackConfig', label: 'Callback Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for the callback experience' },
        // --- Queue Status ---
        { key: 'queueStatusEnabled', label: 'Queue Status Enabled', type: 'boolean', defaultValue: false, helpText: 'Inform customer of estimated wait time and position in queue' },
        { key: 'queueStatusConfig', label: 'Queue Status Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for queue status information' },
        // --- Post Call Survey ---
        { key: 'postCallSurveyEnabled', label: 'Post Call Survey Enabled', type: 'boolean', defaultValue: false, helpText: 'Turns on post call survey for this queue' },
        { key: 'postCallSurveyConfig', label: 'Post Call Survey Config', type: 'reference', referencedConfigType: 'queue-features', helpText: 'Configuration for the post call survey' },
        // --- Recording & Analytics ---
        { key: 'dynamicCallerID', label: 'Dynamic Caller ID', type: 'boolean', defaultValue: false, helpText: 'Turns on dynamic caller ID for this queue' },
        { key: 'callRecordingEnabled', label: 'Call Recording', type: 'boolean', defaultValue: true, helpText: 'Turns on call recording for this queue' },
        { key: 'contactLensRedaction', label: 'Contact Lens Redaction', type: 'boolean', defaultValue: false, helpText: 'Enables Contact Lens redaction' },
        { key: 'contactLensEnabled', label: 'Contact Lens', type: 'boolean', defaultValue: false, helpText: 'Enables Contact Lens transcription' },
        { key: 'outboundCallerId', label: 'Outbound Caller ID', type: 'text', helpText: 'Number displayed as caller ID when masking is enabled' },
        { key: 'chatCustomerQueueLoopDurationInSeconds', label: 'Chat Queue Loop Duration (seconds)', type: 'number', helpText: 'Loop wait time in seconds for customer queue waiting loop' },
        { key: 'tagAttributesToCTR', label: 'Tag Attributes to CTR', type: 'text', helpText: 'Comma-separated list of contact attributes to tag to the CTR' },
      ],
    },
  ],
  [
    'custom',
    {
      configType: 'custom',
      displayName: 'Custom',
      description: 'A custom configuration with no predefined schema — use the JSON editor',
      payloadSchema: [],
    },
  ],
]);

/**
 * Get all available config types as an array (for dropdowns).
 * Excludes sub-type templates (those with a different map key than their configType).
 */
export function getConfigTypes(): { label: string; value: string }[] {
  const seen = new Set<string>();
  const result: { label: string; value: string }[] = [];
  for (const [key, t] of templateRegistry) {
    // Only include entries where the map key matches the configType (canonical entries)
    if (key === t.configType && !seen.has(t.configType)) {
      seen.add(t.configType);
      result.push({ label: t.displayName, value: t.configType });
    }
  }
  return result;
}

/**
 * Get the template for a given configType, or undefined if not found.
 * For 'queue-features', returns the generic (all-fields) template.
 * Sub-type templates are accessed via their specific registry key (e.g. 'queue-features-announcements').
 */
export function getTemplate(configType: string): ConfigTemplate | undefined {
  return templateRegistry.get(configType);
}

/**
 * Maps queue-features sub-type template keys to their EA portal parent paths.
 * These parent paths correspond to the intermediate category items in DynamoDB
 * (e.g., /queue-features/Announcements, /queue-features/Callbacks).
 *
 * When creating a new queue-features config, the _parentPath must be included
 * so the backend constructs a 3-segment path that the list handler can find.
 */
export const QUEUE_FEATURES_PARENT_PATHS: Record<string, string> = {
  'queue-features-announcements': '/queue-features/Announcements',
  'queue-features-callbacks': '/queue-features/Callbacks',
  'queue-features-queue-status': '/queue-features/QueueStatus',
  'queue-features-agent-queue-escalation': '/queue-features/AgentQueueEscalation',
  'queue-features-surveys': '/queue-features/Surveys',
};

/**
 * Resolves the _parentPath for a given template key and configType.
 * For queue-features sub-types, returns the known parent path.
 * For queue-features generic (or unknown sub-type), returns '/queue-features/General'.
 * For all other config types, returns undefined (no _parentPath needed).
 */
export function resolveParentPath(templateKey: string, configType: string): string | undefined {
  if (configType !== 'queue-features') {
    return undefined;
  }
  return QUEUE_FEATURES_PARENT_PATHS[templateKey] ?? '/queue-features/General';
}

/**
 * Get all templates available for the CREATE flow template gallery.
 * Includes sub-type templates but excludes the generic 'queue-features' fallback.
 */
export function getGalleryTemplates(): ConfigTemplate[] {
  return Array.from(templateRegistry.entries())
    .filter(([key, _t]) => {
      // Exclude the generic queue-features (key matches configType but has sub-types)
      if (key === 'queue-features') return false;
      return true;
    })
    .map(([, t]) => t);
}

/**
 * Get gallery templates grouped by templateCategory.
 * Templates without a category are grouped under 'Other'.
 */
export function getGalleryTemplatesByCategory(): Record<string, ConfigTemplate[]> {
  const templates = getGalleryTemplates();
  const grouped: Record<string, ConfigTemplate[]> = {};
  for (const t of templates) {
    const category = t.templateCategory ?? 'Other';
    if (!grouped[category]) grouped[category] = [];
    grouped[category].push(t);
  }
  return grouped;
}
